import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { createSafeContext } from '@mews/framework';
import { BreakpointRangeProvider, matchesBreakpointRange } from '@optimus-web/core';
import BreakpointRange from './breakpointRange';

/** This is used by the split panel to revert back to default breakpoint range state */
export const getBreakpointRangeDefault = () =>
    BreakpointRange.enumValues.find((b) => matchesBreakpointRange(b.value)) || BreakpointRange.Phone;

interface CommanderBreakpointRangeProviderProps {
    children?: React.ReactNode;
    getBreakpointRange?: typeof getBreakpointRangeDefault;
}

interface CommanderBreakPointRangeContextProps {
    breakPointRange: typeof getBreakpointRangeDefault;
}

interface CommanderBreakPointRangeApiContextProps {
    setCommanderBreakpointRange: Dispatch<SetStateAction<CommanderBreakPointRangeContextProps>>;
}

const { CommanderBreakPointRangeDataContextProvider } = createSafeContext<
    'CommanderBreakPointRangeDataContext',
    CommanderBreakPointRangeContextProps
>('CommanderBreakPointRangeDataContext');

const { CommanderBreakPointRangeApiContextProvider, useCommanderBreakPointRangeApiContext } = createSafeContext<
    'CommanderBreakPointRangeApiContext',
    CommanderBreakPointRangeApiContextProps
>('CommanderBreakPointRangeApiContext');

export const useCommanderBreakPointRangeApi = useCommanderBreakPointRangeApiContext;

export const CommanderBreakpointRangeProvider = ({
    children,
    getBreakpointRange = getBreakpointRangeDefault,
}: CommanderBreakpointRangeProviderProps) => {
    const [commanderBreakpointRangeContext, setCommanderBreakpointRangeContext] = useState({
        breakPointRange: getBreakpointRange,
    });
    const contextValue: CommanderBreakPointRangeContextProps = useMemo(
        () => ({
            breakPointRange: commanderBreakpointRangeContext.breakPointRange,
        }),
        [commanderBreakpointRangeContext.breakPointRange]
    );

    const apiContextValue: CommanderBreakPointRangeApiContextProps = useMemo(
        () => ({
            setCommanderBreakpointRange: setCommanderBreakpointRangeContext,
        }),
        [setCommanderBreakpointRangeContext]
    );

    return (
        <CommanderBreakPointRangeDataContextProvider value={contextValue}>
            <CommanderBreakPointRangeApiContextProvider value={apiContextValue}>
                <BreakpointRangeProvider getBreakpointRange={contextValue.breakPointRange || getBreakpointRangeDefault}>
                    {children}
                </BreakpointRangeProvider>
            </CommanderBreakPointRangeApiContextProvider>
        </CommanderBreakPointRangeDataContextProvider>
    );
};
